import React from "react";
import { StatusBadge } from "../design-system";
import type { AgentOsRegistryControlRowView } from "../api";

function statusLabel(status: AgentOsRegistryControlRowView["status"]): string {
  const labels: Record<AgentOsRegistryControlRowView["status"], string> = {
    live_control_ready: "Live Control",
    guided_builder_ready: "Builder geführt",
    draft_lane_ready: "Draft Lane",
    proposal_only: "Proposal only",
    read_only: "Read-only",
    unavailable: "Nicht verfügbar",
  };
  return labels[status];
}

function statusTone(status: AgentOsRegistryControlRowView["status"]): "success" | "info" | "warning" | "neutral" | "blocked" {
  if (status === "live_control_ready") return "success";
  if (status === "guided_builder_ready" || status === "draft_lane_ready") return "info";
  if (status === "proposal_only") return "warning";
  if (status === "unavailable") return "blocked";
  return "neutral";
}

function boundaryLabel(boundary: AgentOsRegistryControlRowView["writeBoundary"]): string {
  const labels: Record<AgentOsRegistryControlRowView["writeBoundary"], string> = {
    unified_change_lane: "Unified Change Lane",
    guided_builder_without_direct_lane: "Builder ohne eigenen Entity-Typ",
    proposal_only: "Nur Proposal",
    read_only_attestation: "Read-only Attestation",
    not_yet_supported: "Noch nicht unterstützt",
  };
  return labels[boundary];
}

export function AgentOsRegistryLiveControlPanel({ rows }: { rows: AgentOsRegistryControlRowView[] }): JSX.Element {
  const live = rows.filter((row) => row.supportsUnifiedMutationLane).length;
  const guarded = rows.filter((row) => row.status === "guided_builder_ready" || row.status === "draft_lane_ready").length;
  const readOnly = rows.filter((row) => row.status === "read_only" || row.status === "proposal_only" || row.status === "unavailable").length;
  return (
    <section className="mb-4 rounded-lg border border-white/10 bg-black/15 p-4" aria-label="Registry Live Control Coverage">
      <div className="flex min-w-0 flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] uppercase tracking-[0.14em] text-jarvis-accent">Registry Live Control</p>
          <h3 className="mt-1 text-sm font-semibold text-jarvis-text">Roadmap-Coverage ohne Fake-GO</h3>
          <p className="mt-1 max-w-3xl text-xs leading-relaxed text-jarvis-muted">
            Jede Registry-Kategorie zeigt, ob sie wirklich über die Unified Change Lane steuerbar ist, nur geführt/proposal-basiert arbeitet oder bewusst read-only bleibt.
          </p>
        </div>
        <div className="grid grid-cols-3 gap-1.5 text-center text-[10px]">
          <div className="rounded-md border border-jarvis-accent/25 bg-jarvis-accent/10 px-2 py-1"><p className="font-semibold text-jarvis-text">{live}</p><p className="text-jarvis-muted">live</p></div>
          <div className="rounded-md border border-sky-400/20 bg-sky-400/10 px-2 py-1"><p className="font-semibold text-jarvis-text">{guarded}</p><p className="text-jarvis-muted">geführt</p></div>
          <div className="rounded-md border border-white/10 bg-white/5 px-2 py-1"><p className="font-semibold text-jarvis-text">{readOnly}</p><p className="text-jarvis-muted">offen</p></div>
        </div>
      </div>
      <div className="simo-thin-scroll mt-3 overflow-x-auto">
        <table className="min-w-[980px] w-full text-left text-[11px]">
          <thead className="text-[10px] uppercase tracking-[0.12em] text-jarvis-muted">
            <tr>
              <th className="border-b border-white/10 px-2 py-2">Kategorie</th>
              <th className="border-b border-white/10 px-2 py-2">Status</th>
              <th className="border-b border-white/10 px-2 py-2">Lane</th>
              <th className="border-b border-white/10 px-2 py-2">Builder / Quelle</th>
              <th className="border-b border-white/10 px-2 py-2 text-right">Records</th>
              <th className="border-b border-white/10 px-2 py-2 text-right">Mutation-ready</th>
              <th className="border-b border-white/10 px-2 py-2">Evidence</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id} className="align-top">
                <td className="border-b border-white/5 px-2 py-2 font-medium text-jarvis-text">{row.label}</td>
                <td className="border-b border-white/5 px-2 py-2"><StatusBadge status={row.status} label={statusLabel(row.status)} tone={statusTone(row.status)} /></td>
                <td className="border-b border-white/5 px-2 py-2 text-jarvis-muted">
                  <p>{boundaryLabel(row.writeBoundary)}</p>
                  <p className="mt-1 font-mono text-[10px]">{row.mutationLane}</p>
                </td>
                <td className="border-b border-white/5 px-2 py-2 text-jarvis-muted">
                  <p className="text-jarvis-text">{row.builder ?? "—"}</p>
                  <p className="mt-1">{row.runtimeSource ?? "keine Runtime-Quelle"}</p>
                </td>
                <td className="border-b border-white/5 px-2 py-2 text-right font-mono text-jarvis-text">{row.recordCount}</td>
                <td className="border-b border-white/5 px-2 py-2 text-right font-mono text-jarvis-text">{row.mutationReadyCount}</td>
                <td className="border-b border-white/5 px-2 py-2 text-jarvis-muted">
                  <p>{row.reason}</p>
                  <p className="mt-1 break-all font-mono text-[10px]">{row.evidence.join(" · ")}</p>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
